import {
  Chip,
  Collapse,
  Divider,
  IconButton,
  Paper,
  Tooltip,
  Typography
} from '@material-ui/core';
import {
  Theme,
  createStyles,
  fade,
  makeStyles
} from '@material-ui/core/styles';
import {
  ExpandLessRounded,
  ExpandMoreRounded,
  PersonAddRounded,
  PersonRounded,
  ShareRounded
} from '@material-ui/icons';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { RootState } from '../app/store';
import useDeviceScreen from '../hooks/useDeviceScreen';
import { ProjectDocument } from '../pages/ProjectDocument/projectDocumentSlice';
import { ProjectDocumentRevision } from './../pages/ProjectDocumentRevision/projectDocumentRevisionSlice';

export default function DocRevisionInfoCard({
  document,
  docRevision
}: {
  document: ProjectDocument;
  docRevision: ProjectDocumentRevision;
}) {
  const classes = useStyles();
  const { IS_TABLET } = useDeviceScreen();
  const { t } = useTranslation();

  const { user } = useSelector((state: RootState) => state.userProfile);
  const { data: disciplines } = useSelector(
    (state: RootState) => state.disciplines
  );
  const { data: purposes } = useSelector((state: RootState) => state.purposes);
  const { data: revisions } = useSelector(
    (state: RootState) => state.revisions
  );

  const documentDiscipline = disciplines.find(
    (docDiscipline) => docDiscipline.id === document.disciplineId
  );
  const documentPurpose = purposes.find(
    (docPurpose) => docPurpose.id === docRevision.purposeId
  );
  const documentRevision = revisions.find(
    (docRev) => docRev.id === docRevision.revisionId
  );

  const [openDetail, setOpenDetail] = useState<boolean>(false);
  const customDocument =
    document.projectDocumentCustomAccessControls.length > 0;

  return (
    <div>
      <Paper className={classes.card}>
        <div className={classes.header}>
          <Typography
            className={classes.headerText}
            variant="subtitle1"
            gutterBottom
          >
            {t('docRevisionInfoCard.documentInformation') as string}
          </Typography>
          <IconButton
            className={classes.isMobile}
            size="small"
            onClick={() => setOpenDetail(!openDetail)}
          >
            {openDetail ? <ExpandLessRounded /> : <ExpandMoreRounded />}
          </IconButton>
        </div>
        <Collapse in={IS_TABLET && !openDetail}>
          <Typography variant="subtitle2">{document?.name}</Typography>
          <Typography variant="subtitle2">
            {t('docRevisionInfoCard.purpose')}: {documentPurpose?.abbreviation} {t('docRevisionInfoCard.revision')}:{' '}
            {documentRevision?.name}
          </Typography>
        </Collapse>
        <Collapse in={!IS_TABLET || openDetail}>
          <Divider className={classes.divider} />
          <Typography variant="subtitle2">{t('docRevisionInfoCard.documentName') as string}</Typography>
          <Typography variant="body1" gutterBottom>
            {document?.name}
          </Typography>
          <Typography variant="subtitle2">{t('docRevisionInfoCard.documentNo') as string}</Typography>
          <Typography variant="body1" gutterBottom>
            {document?.no}
          </Typography>
          <Typography variant="subtitle2">{t('docRevisionInfoCard.discipline') as string}</Typography>
          <Typography variant="body1" gutterBottom>
            {documentDiscipline?.name}
          </Typography>
          <div className={classes.flex}>
            <div className={classes.flexGrow}>
              <Typography variant="subtitle2">{t('docRevisionInfoCard.purpose') as string}</Typography>
              <Typography variant="body1" gutterBottom>
                {documentPurpose?.abbreviation}
              </Typography>
            </div>
            <div className={classes.flexGrow}>
              <Typography variant="subtitle2">{t('docRevisionInfoCard.revision') as string}</Typography>
              <Typography variant="body1" gutterBottom>
                {documentRevision?.name}
              </Typography>
            </div>
          </div>
          <div className={classes.alignItemCenterRight}>
            <Tooltip
              title={`${document.accessControl.name} ${t('docRevisionInfoCard.document')}${
                customDocument ? ` ${t('docRevisionInfoCard.withSpecificSharing')}` : ''
              }`}
              arrow
            >
              <Chip
                variant="outlined"
                size="small"
                icon={<ShareRounded />}
                label={
                  <span className={classes.labelWithIcon}>
                    {document.accessControl.name}
                    {customDocument && (
                      <PersonAddRounded
                        className={classes.iconAtEnd}
                        color="action"
                      />
                    )}
                  </span>
                }
              />
            </Tooltip>
            {document.documentCreatorId === user?.id && (
              <Tooltip title={t('docRevisionInfoCard.myDocument') as string} arrow>
                <PersonRounded
                  className={classes.myDocumentIcon}
                  color="action"
                />
              </Tooltip>
            )}
          </div>
        </Collapse>
      </Paper>
    </div>
  );
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    card: {
      marginBottom: theme.spacing(1.5),
      padding: theme.spacing(1.5),
      borderRadius: theme.spacing(2)
    },
    title: {
      display: 'flex',
      alignItems: 'center'
    },
    header: {
      display: 'flex',
      alignItems: 'center'
    },
    headerText: {
      flexGrow: 1,
      lineHeight: '125%'
    },
    divider: {
      margin: theme.spacing(0.5, 0, 1.5, 0),
      width: theme.spacing(4)
    },
    isMobile: {
      [theme.breakpoints.up('md')]: {
        display: 'none'
      }
    },
    flex: {
      display: 'flex'
    },
    flexGrow: {
      flexGrow: 1
    },
    alignItemCenterRight: {
      display: 'flex',
      justifyContent: 'flex-end',
      alignItems: 'center'
    },
    labelWithIcon: {
      display: 'flex',
      alignItems: 'center'
    },
    iconAtEnd: {
      marginLeft: theme.spacing(0.5),
      width: 18,
      height: 18
    },
    myDocumentIcon: {
      width: 24,
      height: 24,
      border: '1px solid',
      borderColor: fade(theme.palette.divider, 0.25),
      borderRadius: 16,
      marginLeft: theme.spacing(0.5),
      padding: theme.spacing(0.25)
    }
  })
);
